import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectAllPeopleMembersFromState } from 'src/app/BusinessRules/redux/businessRules.selectors'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import OnOffSwitch from 'src/components/OnOffSwitch/OnOffSwitch'
import SvgExternalLink from 'src/components/Svgs/SvgExternalLink'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import Tooltip from 'src/components/Tooltip/Tooltip'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
} from 'src/components/UserDropdown/UserDropdown'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { translateSingleString, useTranslate } from 'src/localization/Trns'
import { OrganizationsListModel } from 'src/models/organizations.models'
import { UserListModel } from 'src/models/user.models'
import {
  orgOutOfPocketExpensesMutations,
  orgOutOfPocketExpensesQueryKeys,
} from 'src/queries/organizations/outOfPocketExpenses/outOfPocketExpenses.queries'
import { orgQueryKeys } from 'src/queries/queries.constants'
import { createPutOrganizationSuccessAction } from 'src/redux/actions/data/organizations.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type OutOfPocketExpensesFields = {
  isVisible?: boolean
  expenseApproverId?: string
  mileageRate?: number
}

interface OutOfPocketExpensesFormProps {
  closeOutOfPocketExpensesForm: () => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  organization: OrganizationsListModel
  approver?: UserListModel
}

const OutOfPocketExpensesForm: React.FC<OutOfPocketExpensesFormProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const allMemberIdOptions = useSelector(selectAllPeopleMembersFromState)
  const { analytics } = useAnalyticsContext()

  const {
    closeOutOfPocketExpensesForm,
    userInfiniteScrollProps,
    organization,
    approver,
  } = props
  const putOutOfPocketExpenses = useMutation({
    ...orgOutOfPocketExpensesMutations.put(),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        orgOutOfPocketExpensesQueryKeys.enabled(variables.orgId),
        data.organization.isOutOfPocketExpensesVisible,
      )
      queryClient.invalidateQueries({
        queryKey: orgQueryKeys.detail(variables.orgId),
      })
      dispatch(createPutOrganizationSuccessAction(data))
      closeOutOfPocketExpensesForm()
    },
  })
  const loading = putOutOfPocketExpenses.isPending
  const errorMessage = putOutOfPocketExpenses.error?.error

  const manageOutOfPocketExpensesFieldsValidation =
    (): FormFieldsValidation<OutOfPocketExpensesFields> => ({
      expenseApproverId: [stringIsEmpty()],
    })

  const {
    fields: outOfPocketExpensesFlatFields,
    onFieldChange: outOfPocketExpensesFlatFieldChange,
  } = useFormFields<OutOfPocketExpensesFields>(
    {
      expenseApproverId: approver?.id,
      isVisible: organization.isOutOfPocketExpensesVisible,
      mileageRate: organization.mileageRateThousandths,
    },
    manageOutOfPocketExpensesFieldsValidation,
  )

  const selectedMember = outOfPocketExpensesFlatFields.expenseApproverId
    ? new Set<string>([outOfPocketExpensesFlatFields.expenseApproverId])
    : new Set<string>()

  const handleOnCancel = (): void => {
    closeOutOfPocketExpensesForm()
  }

  const handleMileageInputChange = (val: string): void => {
    if ((/^(\d+)?(?:\.\d{1,3})?$/.test(val) && val.length < 10) || val === '') {
      outOfPocketExpensesFlatFieldChange(
        'mileageRate',
        val === '' ? undefined : val,
      )
    }
  }

  const handleOnSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    if (
      outOfPocketExpensesFlatFields.isVisible !== undefined ||
      outOfPocketExpensesFlatFields.expenseApproverId !== undefined
    ) {
      putOutOfPocketExpenses.mutate({
        orgId: organization.id,
        body: outOfPocketExpensesFlatFields,
      })
    }
  }

  return (
    <div>
      <form
        onSubmit={handleOnSubmit}
        css={css`
          display: flex;
          width: 100%;
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            width: 100%;
            row-gap: 40px;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                12,
                styleConstants.colors.gray_darkest,
                styleConstants.fontFamilies.regular,
              )}
            `}
          >
            {translate(
              'Assign an Owner, Admin, or Bookkeeper to approve reimbursement requests across your organization.',
            )}
          </span>
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  14,
                  styleConstants.colors.gray_darkest,
                  styleConstants.fontFamilies.regular,
                )};
                display: flex;
                margin-bottom: 8px;
              `}
            >
              {translate('Reimbursements')}
            </span>
            <div
              css={css`
                display: flex;
                width: 175px;
              `}
            >
              <OnOffSwitch
                defaultOnOffState={organization.isOutOfPocketExpensesVisible}
                onChange={(val: boolean): void => {
                  outOfPocketExpensesFlatFieldChange('isVisible', val)
                }}
                testId={'OOPESwitch'}
              />
            </div>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            <UserDropdown
              isCreatable={false}
              placeholder={translateSingleString(
                locale,
                'Type or select from list',
              )}
              fieldLabel={
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )};
                    margin-bottom: 8px;
                    display: flex;
                    align-items: center;
                  `}
                >
                  {translate('Approver')}
                </span>
              }
              chooseOption={(val: string): void => {
                outOfPocketExpensesFlatFieldChange('expenseApproverId', val)
              }}
              selectedValues={selectedMember}
              optionGroups={[
                {
                  options: allMemberIdOptions,
                },
              ]}
              infiniteScrollProps={userInfiniteScrollProps}
              allowNone
              css={css`
                width: 100%;
                min-height: 110px;
              `}
              data-testid={'OOPEApproverUserDropdown'}
            />
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              row-gap: 16px;
            `}
          >
            <span
              css={css`
                ${getFontCss(
                  16,
                  styleConstants.colors.black,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {translate('Transportation Rules')}
            </span>
            <div
              css={css`
                display: flex;
                flex-direction: column;
              `}
            >
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <FieldLabel>{translate('Mileage Rate')}</FieldLabel>
                  <Tooltip
                    openLeft={false}
                    css={css`
                      margin-bottom: 6px;
                    `}
                  >
                    {translate(
                      'Set the rate at which you’ll reimburse your employees for miles driven',
                    )}
                  </Tooltip>
                </div>
                <HoverableLink
                  ariaLabel={'See current mileage rates'}
                  href={
                    'https://www.irs.gov/tax-professionals/standard-mileage-rates'
                  }
                >
                  <span
                    css={css`
                      display: flex;
                      align-items: center;
                      ${getFontCss(
                        14,
                        themeConstants.colors.cta,
                        styleConstants.fontFamilies.regular,
                      )};
                    `}
                  >
                    {translate('See current mileage rates')}
                    <SvgExternalLink
                      css={css`
                        transform: scale(calc(16 / 24));
                      `}
                    />
                  </span>
                </HoverableLink>
              </div>
              <TextInputField
                design={TextInputFieldDesigns.New}
                placeholder={translateSingleString(
                  locale,
                  'For example, $0.725',
                )}
                value={outOfPocketExpensesFlatFields['mileageRate']}
                onChange={handleMileageInputChange}
                type={'number'}
                disableWheel
                step={0.001}
                disableExponent
                disablePositiveAndNegative
                testId={'mileageRateOOPE'}
              />
            </div>
          </div>
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            {errorMessage && (
              <ErrorList
                errors={[errorMessage]}
                css={css`
                  padding: 0px;
                  margin-bottom: 10px;
                `}
              />
            )}
            <div
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <Button
                onClick={(): void => {
                  analytics?.track('org settings out of pocket expenses', {
                    enabled: outOfPocketExpensesFlatFields.isVisible,
                  })
                }}
                type={ButtonTypes.submit}
                design={ButtonDesigns.designCta}
                text={translate('Save')}
                big
                loading={loading}
                css={css`
                  align-self: flex-start;
                `}
                testId={'saveOOPEBtn'}
              />
              <HoverableLink
                ariaLabelSource={'Cancel'}
                onClick={handleOnCancel}
                css={css`
                  margin-left: 20px;
                `}
                testId={'cancelOOPEBtn'}
              >
                {translate('Cancel')}
              </HoverableLink>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}

export default OutOfPocketExpensesForm
